import { Autocomplete, TextField } from '@mui/material';
import { DataAutocomplete } from '../SelectResponsibles';
import { GenericTable } from '../Table';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  CreateCreditOperation,
  CreditData,
  ResponsibleCreditSummaryDto,
  UserRole,
} from '../../services/api.types';
import { apiService } from '../../services/api';
import { formatMoney } from '../../utils/formatters';
import dayjs from 'dayjs';
import { useAppSelector } from '../../hooks';
import { EditCreditModal } from '../EditCreditsModal';
import { Button } from '../Button';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import { ResponsibleCreditsSummary } from '../ResponsibleCreditsSummary';
import { ListAlt } from '@mui/icons-material';
import { Modal } from '../../components/Modal';
import { CreditDetailsModal } from '../CreditDetailsModal';
import { Select } from '../Select';
import { ReportsListModal } from '../ReportsListModal';

interface CreditsTableProps {
  isFetchingUser: boolean;
  responsibles: DataAutocomplete[];
  isSelectedPanel: boolean;
}

export const CreditsTable = ({
  isFetchingUser,
  responsibles,
  isSelectedPanel,
}: CreditsTableProps) => {
  const user = useAppSelector((state) => state.user);
  const isAdminOrEmployee =
    user?.role === UserRole.ADMIN || user?.role === UserRole.EMPLOYEE;
  const [selectedResponsible, setSelectedResponsible] =
    useState<DataAutocomplete | null>();
  const [isFetchingCredits, setIsFetchingCredits] = useState<boolean>(false);
  const [isFetchingCreditsSummary, setIsFetchingCreditsSummary] =
    useState<boolean>(false);
  const [openEditCreateModal, setOpenEditCreateModal] =
    useState<boolean>(false);
  const [isOpenCreditSummary, setIsOpenCreditSummary] =
    useState<boolean>(false);

  const [credits, setCredits] = useState<CreditData[]>([]);
  const [filterOption, setFilterOption] = useState<string>('');
  const [creditSummary, setCreditSummary] = useState<
    ResponsibleCreditSummaryDto | undefined
  >();
  const [openDetailsModal, setOpenDetailsModal] = useState<boolean>(false);
  const [selectedCreditId, setSelectedCreditId] = useState<number>();
  const selectedCredit = useMemo(
    () => credits.find((c) => c.id === selectedCreditId),
    [credits, selectedCreditId]
  );

  const [openReportsList, setOpenReportsList] = useState<boolean>(false);

  const clickRef = useRef(250);

  const handleFilterChange = (value: string) => {
    setFilterOption(value);
    setSelectedCreditId(0);
  };

  const handleSelect = (id: number) => {
    setSelectedCreditId(id);
    handleOpen(setOpenDetailsModal);
  };

  const handleOpen = (
    setState: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    window.clearTimeout(clickRef?.current);
    clickRef.current = window.setTimeout(() => {
      setState(true);
    }, 150);
  };

  const handleClose = (
    setState: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    window.clearTimeout(clickRef?.current);
    clickRef.current = window.setTimeout(() => {
      setState(false);
    }, 100);
  };

  const showCreditSummary = selectedResponsible && creditSummary;

  const handleCreditSummaryClose = () => setIsOpenCreditSummary(false);
  const handleCreditSummaryOpen = () => setIsOpenCreditSummary(true);

  const updateCredits = useCallback(async () => {
    if (!selectedResponsible) return;

    setIsFetchingCredits(true);
    await apiService
      .getAllCreditsByResponsible(selectedResponsible.id)
      .then((res) => {
        setCredits(res);
      })
      .finally(() => setIsFetchingCredits(false));
  }, [selectedResponsible]);

  const updateCreditsSummary = useCallback(async () => {
    if (!selectedResponsible) return;

    setIsFetchingCreditsSummary(true);
    apiService
      .getResponsibleCreditSummary(selectedResponsible.id)
      .then((res) => {
        setCreditSummary(res);
      })
      .finally(() => setIsFetchingCreditsSummary(false));
  }, [selectedResponsible]);

  const rowsCreditsTable = useMemo(() => {
    return credits.map((credit) => ({
      id: credit.id,
      Responsável: credit.receiverName,
      Valor: formatMoney(credit.value, true),
      'Saldo após transação': formatMoney(credit.currentBalance, true),
      Data: dayjs(credit.createdAt).format('DD/MM/YYYY [às] HH:mm'),
    }));
  }, [credits]);

  const onCloseCreateCreditModal = () => {
    setOpenEditCreateModal(false);
  };

  const onOpenModalCreateCredit = () => {
    setOpenEditCreateModal(true);
  };

  const handleCreditCreation = async () => {
    setOpenEditCreateModal(false);
    await updateCredits();
    await updateCreditsSummary();
  };

  useEffect(() => {
    if (user?.role === UserRole.RESPONSIBLE) {
      setSelectedResponsible(responsibles[0]);
    }
  }, [responsibles, user?.role]);

  useEffect(() => {
    if (selectedResponsible && isSelectedPanel) {
      updateCredits();
      updateCreditsSummary();
    } else {
      setCredits([]);
    }
  }, [
    selectedResponsible,
    isSelectedPanel,
    updateCredits,
    updateCreditsSummary,
  ]);

  return (
    <div className="max-h-full flex gap-6">
      <GenericTable
        rows={rowsCreditsTable}
        getSummary={(row) => `${row.Valor} - ${row.Data}`}
        Footer={<div></div>}
        isSelectable
        onSelectRow={(credit) => {
          handleSelect(credit.id);
        }}
        isFetching={isFetchingCredits}
        AboveHeader={
          <div
            className={clsx(
              'flex flex-col lg:flex-row',
              'w-full gap-4 justify-between'
            )}
          >
            {isAdminOrEmployee && (
              <div
                className={clsx(
                  'flex flex-col lg:flex-row',
                  'w-full gap-4 justify-between'
                )}
              >
                <div className="w-full md:w-3/5">
                  <Autocomplete
                    size="small"
                    noOptionsText={'Sem opções'}
                    value={selectedResponsible}
                    onChange={(
                      event: object,
                      newValue: DataAutocomplete | null
                    ) => {
                      setSelectedResponsible(newValue);
                    }}
                    getOptionLabel={(option) => option.displayName}
                    isOptionEqualToValue={(option, value) =>
                      option.displayName === value.displayName
                    }
                    loadingText="Carregando..."
                    loading={isFetchingUser}
                    options={responsibles}
                    renderInput={(params) => (
                      <TextField {...params} label="Selecione um Usuário" />
                    )}
                  />
                </div>
                <Button
                  size="small"
                  circular
                  className="w-min whitespace-nowrap"
                  onClick={onOpenModalCreateCredit}
                  startIcon={<LocalAtmIcon />}
                >
                  {'EDITAR CRÉDITO'}
                </Button>
              </div>
            )}
            {showCreditSummary && (
              <div className="xl:hidden self-end">
                <Button
                  circular
                  startIcon={<ListAlt />}
                  onClick={handleCreditSummaryOpen}
                >
                  Resumo
                </Button>
              </div>
            )}
            <Select
              id="Select"
              size="small"
              label="Filtrar por"
              value={filterOption}
              onChange={handleFilterChange}
              className={clsx('w-full md:w-3/5')}
              options={[
                { value: 'All', label: 'Todos' },
                { value: CreateCreditOperation.ADD, label: 'Adições' },
                { value: CreateCreditOperation.REMOVE, label: 'Retiradas' },
              ]}
            />
          </div>
        }
      />

      {openEditCreateModal && (
        <EditCreditModal
          open={openEditCreateModal}
          onClose={onCloseCreateCreditModal}
          defaultUser={selectedResponsible}
          onCreditCreation={handleCreditCreation}
        />
      )}

      {showCreditSummary && (
        <>
          <ResponsibleCreditsSummary
            className="hidden xl:flex"
            onOpenPredictedReports={() => setOpenReportsList(true)}
            creditSummary={creditSummary}
            isLoading={isFetchingCreditsSummary}
          />
          <Modal
            className="xl:hidden"
            open={isOpenCreditSummary}
            onClose={handleCreditSummaryClose}
            transparentBackground
          >
            <ResponsibleCreditsSummary
              onClose={handleCreditSummaryClose}
              onOpenPredictedReports={() => setOpenReportsList(true)}
              creditSummary={creditSummary}
              isLoading={isFetchingCreditsSummary}
            />
          </Modal>
        </>
      )}

      {openDetailsModal && selectedCredit && (
        <CreditDetailsModal
          open={openDetailsModal}
          selectedCredit={selectedCredit}
          onClose={() => handleClose(setOpenDetailsModal)}
        />
      )}

      <ReportsListModal
        title="Relatórios"
        onClose={() => setOpenReportsList(false)}
        reports={creditSummary?.predictedMonthlyPaymentReports ?? []}
        open={openReportsList}
      />
    </div>
  );
};
